/* COB2B-635 — B2B Pricing Promotion Messages
 *
 * Shared rule-matching engine for storefront discount messaging on PLP, PDP, and Cart.
 * Reads window.CeresB2BDiscountData (rendered from Company metafields in theme.liquid)
 * and decorates DOM nodes that opt in via data-attributes.
 *
 * The actual discount calculation is performed by the WASM functions at checkout
 * (Group 5 / Group 6). This module is display-only.
 */

const DEFAULT_FACTOR = 1;
const PLP_MESSAGE_MAX_LENGTH = 25;

const SOURCE = {
  GROUP5_FLAT: 'group5_flat',
  GROUP5_TIER: 'group5_tier',
  GROUP6_BUNDLE: 'group6_bundle',
};

function isFlatRule(rule) {
  const n = Number(rule.N || 0);
  const x = Number(rule.X || 0);
  return n <= 0 && x <= 0;
}

function quantityInRange(qty, rule) {
  const n = Number(rule.N || 0);
  const x = Number(rule.X || 0);
  if (qty < n) return false;
  if (x > 0 && qty >= x) return false;
  return true;
}

function asString(v) {
  return v == null ? '' : String(v);
}

function buildIndex(rules) {
  const byVariant = new Map();
  const byProduct = new Map();
  const byGroup = new Map();
  const all = [];

  (rules || []).forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const rule = {
      I: asString(raw.I),
      V: asString(raw.V),
      G: asString(raw.G),
      D: Number(raw.D || 0),
      N: Number(raw.N || 0),
      X: Number(raw.X || 0),
      M: raw.M ? String(raw.M) : '',
      Tg: raw.Tg ? String(raw.Tg).toUpperCase() : '',
    };
    if (rule.D <= 0) return;
    all.push(rule);
    if (rule.V) {
      if (!byVariant.has(rule.V)) byVariant.set(rule.V, []);
      byVariant.get(rule.V).push(rule);
    } else if (rule.I) {
      if (!byProduct.has(rule.I)) byProduct.set(rule.I, []);
      byProduct.get(rule.I).push(rule);
    } else if (rule.G) {
      if (!byGroup.has(rule.G)) byGroup.set(rule.G, []);
      byGroup.get(rule.G).push(rule);
    }
  });

  return { byVariant, byProduct, byGroup, all };
}

/* True when checkout can reach `rule`: some quantity in its range gets no
 * discount from the more specific levels' rules (`above`). A gap can only start
 * at the range start or where one of those rules ends (X). */
function fillsGap(rule, above) {
  const start = Math.max(1, Number(rule.N || 0));
  const end = Number(rule.X || 0) > 0 ? Number(rule.X) : Infinity;
  if (start >= end) return false;
  const points = [start, ...above.map((r) => Number(r.X || 0)).filter((x) => x > start && x < end)];
  return points.some((q) => !above.some((r) => quantityInRange(q, r)));
}

/* Match Group 5 rules for a product/variant/group key. Variant > Product > Group,
 * falling back like the WASM function: a quantity with no rule on one level
 * drops to the next (COB2B-865). A rule shows only where checkout can reach it.
 *   flat  = the best flat rule of the first level that prices 1 unit, so it is
 *           what checkout applies at 1 unit. Null when that is a tier, or none.
 *   tiers = the reachable tier rules, as entered.
 * Returns: { flat: rule|null, tiers: rule[] }
 */
function matchGroup5(index, key) {
  const { variantId, productId, group } = key;
  const pools = [
    variantId ? index.byVariant.get(asString(variantId)) : null,
    productId ? index.byProduct.get(asString(productId)) : null,
    group ? index.byGroup.get(group) : null,
  ];
  let flat = null;
  const tiers = [];
  const above = [];
  for (const pool of pools) {
    if (!pool) continue;
    const oneUnitPriced = above.some((r) => quantityInRange(1, r));
    pool.filter((rule) => fillsGap(rule, above)).forEach((rule) => {
      if (!isFlatRule(rule)) tiers.push(rule);
      else if (!oneUnitPriced && (!flat || rule.D > flat.D)) flat = rule;
    });
    above.push(...pool);
  }
  return {
    flat,
    tiers: tiers.sort((a, b) => a.N - b.N),
  };
}

/* Group 6 only matches by Group. */
function matchGroup6(index, key) {
  const { group } = key;
  if (!group) return { tiers: [] };
  const pool = index.byGroup.get(group) || [];
  return {
    tiers: pool.slice().sort((a, b) => a.N - b.N),
  };
}

function pickTierForQuantity(tiers, qty) {
  let chosen = null;
  for (const t of tiers) {
    if (quantityInRange(qty, t)) {
      chosen = t;
    }
  }
  return chosen;
}

/* Display priority (per SOW 3.2c):
 *  1. Group 5 flat M
 *  2. Group 6 tier M (any matching tier; fall back to first)
 *  3. Group 5 tier M
 *  4. Auto-calculated "X% Off" from Group 5 flat
 *  5. null
 */
function pickPlpMessage({ g5, g6 }) {
  if (g5.flat && g5.flat.M) return { text: g5.flat.M, source: SOURCE.GROUP5_FLAT };
  const g6FirstWithMsg = (g6.tiers || []).find((t) => t.M);
  if (g6FirstWithMsg) return { text: g6FirstWithMsg.M, source: SOURCE.GROUP6_BUNDLE };
  const g5TierWithMsg = (g5.tiers || []).find((t) => t.M);
  if (g5TierWithMsg) return { text: g5TierWithMsg.M, source: SOURCE.GROUP5_TIER };
  if (g5.flat && g5.flat.D > 0) {
    return { text: `${g5.flat.D}% Off`, source: SOURCE.GROUP5_FLAT };
  }
  return null;
}

/* COB2B-840: fixed 'en' locale + narrowSymbol so AUD/NZD always read "$19.20",
 * matching Liquid `money` — the browser locale gave "A$" / "NZD". */
function formatCurrency(amountCents, isoCode) {
  const value = (amountCents || 0) / 100;
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: isoCode || 'NZD',
      currencyDisplay: 'narrowSymbol',
    }).format(value);
  } catch (e) {
    return `$${value.toFixed(2)}`;
  }
}

function applyFlatDiscount(priceCents, percent) {
  const reduced = priceCents * (1 - percent / 100);
  return Math.round(reduced);
}

function calcUnitPriceCents(variantPriceCents, percent, factor) {
  const f = factor && factor > 0 ? factor : DEFAULT_FACTOR;
  const reduced = variantPriceCents * (1 - percent / 100);
  return reduced / f;
}

function quantityRangeLabel(rule) {
  const n = Number(rule.N || 0);
  const x = Number(rule.X || 0);
  if (x > 0) return `${n}-${x - 1}`;
  return `${n}+`;
}

/* COB2B-840: one Volume Pricing table row, e.g. { tier: '1-9 CTN6',
 * detail: '$2.46 per unit | SAVE 7.5%' }. `variant` is a b2b-pdp-discount.liquid
 * entry: price (cents), factor, unitWord and label (the size, e.g. CTN6). */
function volumeTierText(rule, variant, isoCode) {
  const unitCents = calcUnitPriceCents(variant.price, rule.D, Number(variant.factor));
  const size = variant.label ? ` ${variant.label}` : '';
  return {
    tier: `${quantityRangeLabel(rule)}${size}`,
    detail: `${formatCurrency(unitCents, isoCode)} per ${variant.unitWord || 'unit'} | SAVE ${rule.D}%`,
  };
}

function truncate(text, max) {
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, Math.max(0, max - 1))}…`;
}

const CeresB2BDiscount = (function buildSingleton() {
  let group5Index = null;
  let group6Index = null;
  let initialised = false;

  function init() {
    if (initialised) return;
    const data = (typeof window !== 'undefined' && window.CeresB2BDiscountData) || null;
    group5Index = buildIndex(data && data.group5);
    group6Index = buildIndex(data && data.group6);
    initialised = true;
  }

  function isActive() {
    init();
    if (!group5Index && !group6Index) return false;
    return group5Index.all.length > 0 || group6Index.all.length > 0;
  }

  function match(key) {
    init();
    return {
      g5: matchGroup5(group5Index, key),
      g6: matchGroup6(group6Index, key),
    };
  }

  /* Volume Pricing is offered only when the company has a Group 5 tier or a
   * Group 6 bundle for the product. Shared by PLP cards and Saved Lists rows. */
  function hasVolumePricing({ productId, variantId, groupG5, groupG6 }) {
    if (!isActive()) return false;
    const g5 = matchGroup5(group5Index, { productId, variantId, group: groupG5 });
    const g6 = matchGroup6(group6Index, { group: groupG6 });
    return g5.tiers.length > 0 || g6.tiers.length > 0;
  }

  function decoratePlpCard(card) {
    if (!card || card.dataset.b2bDiscountDecorated === '1') return;
    if (!isActive()) return;

    const productId = card.dataset.productId || card.dataset.card || '';
    const variantId = card.dataset.variantId || '';
    const group5 = card.dataset.productGroupG5 || card.dataset.productGroup || '';
    const group6 = card.dataset.productGroupG6 || card.dataset.productGroup || '';
    if (!productId && !variantId && !group5 && !group6) return;

    const g5 = matchGroup5(group5Index, { productId, variantId, group: group5 });
    const g6 = matchGroup6(group6Index, { group: group6 });
    const hasVolume = g5.tiers.length > 0 || g6.tiers.length > 0;
    const noMatch = !g5.flat && g5.tiers.length === 0 && g6.tiers.length === 0;

    // Volume Pricing visibility must be decided for every card — including
    // ones with no match — so the hardcoded Liquid link doesn't bleed through.
    toggleVolumePricingLink(card, hasVolume);

    if (noMatch) {
      card.dataset.b2bDiscountDecorated = '1';
      return;
    }

    const message = pickPlpMessage({ g5, g6 });

    if (message) {
      addPricePill(card, message.text);
    }
    if (g5.flat) {
      applyStrikethrough(card, g5.flat.D);
      applyPerUnitDiscount(card, g5.flat.D);
    }
    tagGstAsOriginal(card);

    card.dataset.b2bDiscountDecorated = '1';
  }

  /* COB2B-785 — Saved Lists rows: the PLP card's promotion treatment (message
   * pill, discounted price over the struck-through original, discounted
   * per-unit price) applied to one row's price.liquid render. `scope` holds
   * that render; it can belong to a DOMParser document. */
  function decoratePriceBlock(scope, { productId, variantId, groupG5, groupG6 }) {
    if (!scope || !isActive()) return;
    const g5 = matchGroup5(group5Index, { productId, variantId, group: groupG5 });
    const g6 = matchGroup6(group6Index, { group: groupG6 });
    if (!g5.flat && g5.tiers.length === 0 && g6.tiers.length === 0) return;

    const message = pickPlpMessage({ g5, g6 });
    if (message) insertPricePill(scope.querySelector('.price'), message.text);
    if (g5.flat) {
      applyStrikethrough(scope, g5.flat.D);
      applyPerUnitDiscount(scope, g5.flat.D);
    }
    tagGstAsOriginal(scope);
  }

  function addPricePill(card, text) {
    const firstRow = card.querySelector('.variant-row');
    if (!firstRow) return;
    insertPricePill(firstRow.querySelector(':scope > .price'), text);
  }

  function insertPricePill(price, text) {
    if (!price) return;
    if (price.querySelector(':scope > .b2b-price-pill')) return;
    const pill = price.ownerDocument.createElement('div');
    pill.className = 'b2b-price-pill';
    pill.textContent = truncate(text, PLP_MESSAGE_MAX_LENGTH);
    price.insertBefore(pill, price.firstChild);
  }

  function tagGstAsOriginal(card) {
    card.querySelectorAll('.price-gst').forEach((g) => g.classList.add('b2b-price-original'));
  }

  function applyStrikethrough(card, percent) {
    const priceContainer = card.querySelector('.price-variant-container, .card__information__volume-pricing-container');
    const targets = card.querySelectorAll('.price .price__regular .price-item--regular, .price .price__container .price-item--regular');
    if (!targets.length) return;
    const isoCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'NZD';
    targets.forEach((node) => {
      if (node.dataset.b2bStrikethroughApplied === '1') return;
      const original = node.textContent.trim();
      const cents = parseMoneyToCents(original);
      if (!Number.isFinite(cents)) return;
      const reduced = applyFlatDiscount(cents, percent);

      // 1. Replace the discounted price IN PLACE inside .price-item--regular.
      node.textContent = formatCurrency(reduced, isoCode);
      node.classList.add('b2b-price-discounted');

      // 2. Tag the variant title ("EACH" / "CTN12" / etc.) with the same
      //    .b2b-price-discounted class so it picks up the Sale-red colour.
      const parent = node.parentElement;
      const variantTitle = parent ? parent.querySelector(':scope > .price-variant-title') : null;
      if (variantTitle) variantTitle.classList.add('b2b-price-discounted');

      // 3. Group the new <s class="b2b-price-original"> strikethrough and
      //    the existing .price-gst into a NEW .price__container sibling, so
      //    the design's right-column "strikethrough / GST FREE" stack is a
      //    single grouped block (instead of strikethrough inside .price__regular
      //    and GST floating off in .price's second flex cell).
      const strike = document.createElement('s');
      strike.className = 'b2b-price-original';
      strike.textContent = original;

      const priceEl = node.closest('.price');
      const existingGst = priceEl ? priceEl.querySelector(':scope > .price-gst') : null;

      const newContainer = document.createElement('div');
      newContainer.className = 'price__container b2b-price-secondary-container';
      newContainer.appendChild(strike);
      if (existingGst) {
        existingGst.classList.add('b2b-price-original');
        newContainer.appendChild(existingGst);
      }
      if (priceEl) priceEl.appendChild(newContainer);

      node.dataset.b2bStrikethroughApplied = '1';
    });
    if (priceContainer) priceContainer.classList.add('b2b-has-strikethrough');
  }

  function toggleVolumePricingLink(card, show) {
    card.querySelectorAll('.card__information__volume-pricing-container').forEach((container) => {
      if (show) {
        container.classList.add('b2b-volume-pricing-visible');
        const link = container.querySelector('.card__information__volume-pricing');
        if (link) {
          const productUrl = card.querySelector('a.full-unstyled-link, a.cart-item__link');
          if (productUrl && productUrl.href) {
            link.setAttribute('href', `${productUrl.href.split('#')[0]}#volume-pricing`);
          }
        }
      } else {
        container.classList.remove('b2b-volume-pricing-visible');
      }
    });
  }

  function applyPerUnitDiscount(card, percent) {
    const isoCode = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'NZD';
    card.querySelectorAll('.price-per-unit').forEach((node) => {
      if (node.dataset.b2bPerUnitApplied === '1') return;
      const text = node.textContent;
      const match = text.match(/(?:NZ\$|AU\$|US\$|\$|€|£)\s?[\d,]*\.?\d+/);
      if (!match) return;
      const original = match[0];
      const cents = parseMoneyToCents(original);
      if (!Number.isFinite(cents)) return;
      const reduced = applyFlatDiscount(cents, percent);
      const newAmount = formatCurrency(reduced, isoCode);
      node.textContent = text.replace(original, newAmount);
      node.classList.add('b2b-price-discounted');
      node.dataset.b2bPerUnitApplied = '1';
    });
  }

  function decoratePdpBlock(block) {
    if (!block || block.dataset.b2bDiscountDecorated === '1') return;
    if (!isActive()) return;

    const productId = block.dataset.productId || '';
    const initialVariantId = block.dataset.variantId || '';
    const g5Group = block.dataset.productGroupG5 || '';
    const g6Group = block.dataset.productGroupG6 || '';
    // COB2B-840: set only when the group's promo- collection exists with products.
    const g6CollectionUrl = block.dataset.g6CollectionUrl || '';
    const hasDeal = !!block.dataset.b2bFlag;

    let variantData = [];
    let isoCode = 'NZD';
    const dataNode = block.querySelector('[data-b2b-variant-data]');
    if (dataNode) {
      try {
        const parsed = JSON.parse(dataNode.textContent);
        variantData = parsed.variants || [];
        isoCode = parsed.currency || isoCode;
      } catch (err) {
        // swallow — render without variant data
      }
    }
    const variantById = new Map(variantData.map((v) => [String(v.id), v]));

    // PDP DOM lives outside .b2b-pdp-discount itself — find the surrounding
    // product section so we can decorate the variant rows and the volume
    // pricing shell that the theme rendered elsewhere in the section.
    const scope = block.closest('section[id^="MainProduct-"], .main-product-section, section') || document;
    const volumeRows = scope.querySelectorAll('.product__volume-variants .volume-variant-row');
    const volumeShell = scope.querySelector('[data-b2b-volume-pricing-shell]');
    let scrolledToShell = false;

    function currentMatch(variantId) {
      const idStr = String(variantId || initialVariantId);
      const variant = variantById.get(idStr) || variantData[0] || { price: 0 };
      return {
        idStr,
        variant,
        g5: matchGroup5(group5Index, { productId, variantId: idStr, group: g5Group }),
        g6: matchGroup6(group6Index, { group: g6Group }),
      };
    }

    function applyVariantRowDiscounts(percent) {
      if (!Number.isFinite(percent) || percent <= 0) return;
      volumeRows.forEach((row) => {
        // Discounted/strikethrough on .price-item--regular (matches PLP logic).
        // Markup: two .price__container siblings under .volume-variant-row__col--info.
        // First container: .price__regular > [.price-item--regular, .price-variant-title] + .volume-variant-row__per-unit.
        // Second container: .b2b-price-secondary-container > .price-gst (and JS-inserted <s>).
        row.querySelectorAll('.price__container .price__regular .price-item--regular').forEach((node) => {
          if (node.dataset.b2bStrikethroughApplied === '1') return;
          const original = node.textContent.trim();
          const cents = parseMoneyToCents(original);
          if (!Number.isFinite(cents)) return;
          const reduced = applyFlatDiscount(cents, percent);
          node.textContent = formatCurrency(reduced, isoCode);
          node.classList.add('b2b-price-discounted');

          const label = row.querySelector('.price__container .price__regular .price-variant-title');
          if (label) label.classList.add('b2b-price-discounted');

          // Insert <s> into the second .price__container (b2b-price-secondary-container),
          // ABOVE the .price-gst so the visual order is [strike] / [GST].
          const strike = document.createElement('s');
          strike.className = 'b2b-price-original';
          strike.textContent = original;
          const secondary = row.querySelector('.volume-variant-row__col--info > .b2b-price-secondary-container');
          if (secondary) {
            const gst = secondary.querySelector(':scope > .price-gst');
            if (gst) {
              secondary.insertBefore(strike, gst);
            } else {
              secondary.appendChild(strike);
            }
          } else if (label && label.parentNode) {
            label.parentNode.insertBefore(strike, label.nextSibling);
          }
          node.dataset.b2bStrikethroughApplied = '1';
        });
        // Tag the .price-gst inside the secondary container with .b2b-price-original
        row.querySelectorAll('.b2b-price-secondary-container > .price-gst').forEach((g) => g.classList.add('b2b-price-original'));
        // Per-unit text inside the first .price__container
        row.querySelectorAll('.volume-variant-row__per-unit').forEach((node) => {
          if (node.dataset.b2bPerUnitApplied === '1') return;
          const text = node.textContent;
          const m = text.match(/(?:NZ\$|AU\$|US\$|\$|€|£)\s?[\d,]*\.?\d+/);
          if (!m) return;
          const reduced = applyFlatDiscount(parseMoneyToCents(m[0]), percent);
          node.textContent = text.replace(m[0], formatCurrency(reduced, isoCode));
          node.classList.add('b2b-price-discounted');
          node.dataset.b2bPerUnitApplied = '1';
        });
      });
    }

    function insertPromoMessage(text) {
      if (!text) return;
      const firstRow = volumeRows[0];
      if (!firstRow) return;
      if (firstRow.querySelector(':scope > .product__b2b-promo-message')) return;
      const info = firstRow.querySelector(':scope > .volume-variant-row__col--info') || firstRow;
      if (info.querySelector(':scope > .product__b2b-promo-message')) return;
      const el = document.createElement('div');
      el.className = 'product__b2b-promo-message';
      el.textContent = text;
      info.insertBefore(el, info.firstChild);
    }

    function populateVolumeShell({ g5, g6, variant }) {
      if (!volumeShell) return;
      const tiers = (g5.tiers.length > 0 ? g5.tiers : g6.tiers);
      const showBundleLink = g6.tiers.length > 0 && !!g6CollectionUrl;

      // Reset shell to header-only
      volumeShell.querySelectorAll(':scope > .volume-pricing__row:not(.volume-pricing__row--header)').forEach((r) => r.remove());
      const existingLink = volumeShell.parentNode && volumeShell.parentNode.querySelector(':scope > .b2b-volume-pricing__view-all');
      if (existingLink) existingLink.remove();

      if (!tiers.length) {
        volumeShell.setAttribute('hidden', '');
        return;
      }

      tiers.forEach((rule) => {
        const { tier, detail } = volumeTierText(rule, variant, isoCode);
        const row = document.createElement('div');
        row.className = 'volume-pricing__row';
        row.innerHTML = `
          <span class="volume-pricing__tier">${escapeHtml(tier)}</span>
          <span class="volume-pricing__detail">${escapeHtml(detail)}</span>
        `;
        volumeShell.appendChild(row);
      });
      volumeShell.removeAttribute('hidden');

      // COB2B-840: the shell is hidden until now, so the browser can't scroll to
      // the #volume-pricing that card and cart links use — do it once, here.
      // scroll-margin-top (_product.scss) keeps it clear of the sticky header.
      if (!scrolledToShell && window.location.hash === `#${volumeShell.id}`) {
        scrolledToShell = true;
        volumeShell.scrollIntoView({ block: 'start' });
      }

      if (showBundleLink && volumeShell.parentNode) {
        const a = document.createElement('a');
        a.className = 'b2b-volume-pricing__view-all';
        a.href = g6CollectionUrl;
        a.textContent = `View all products in ${g6Group}`;
        volumeShell.parentNode.insertBefore(a, volumeShell.nextSibling);
      }
    }

    function render(variantId) {
      const ctx = currentMatch(variantId);
      // Decorate variant rows on the first render only; per-unit/strikethrough
      // are idempotent (guarded by dataset flags). Re-running on variant change
      // won't double-apply.
      if (ctx.g5.flat) applyVariantRowDiscounts(ctx.g5.flat.D);
      const msg = pickPlpMessage({ g5: ctx.g5, g6: ctx.g6 });
      if (msg) insertPromoMessage(msg.text);
      // Toggle the deal-tile class on every PDP container that should pick
      // up the peach background. The .b2b-pdp-discount itself is invisible
      // (data-only), so we also stamp the class onto .product__volume-variants
      // and the volume-pricing shell. COB2B-840: shaded exactly when the PDP
      // badge shows — the same Liquid flag (b2b-product-flag.liquid).
      block.classList.toggle('b2b-deal-tile', hasDeal);
      const variantsWrap = scope.querySelector('.product__volume-variants');
      if (variantsWrap) variantsWrap.classList.toggle('b2b-deal-tile', hasDeal);
      if (volumeShell) volumeShell.classList.toggle('b2b-deal-tile', hasDeal);
      // Populate the volume-pricing shell with real tier/bundle rows
      populateVolumeShell(ctx);
    }

    render(initialVariantId);

    document.addEventListener('variant:change', (event) => {
      const v = event && event.detail && event.detail.variant;
      if (v && v.id) render(v.id);
    });

    block.addEventListener('change', (e) => {
      const target = e.target;
      if (!target) return;
      if (target.matches && target.matches('[name="id"], select[name="id"]')) {
        render(target.value);
      }
    });

    block.dataset.b2bDiscountDecorated = '1';
  }

  // Cart lines are fully server-rendered: main-cart-item.liquid matches the
  // Company discount-messages rules in Liquid (same pattern as
  // b2b-product-flag.liquid) for the row shading, DEAL badge, % OFF pill,
  // discounted price, and Discount amount. No JS decoration needed — cart.js
  // re-renders the section HTML after quantity changes.

  return {
    init,
    isActive,
    match,
    hasVolumePricing,
    decoratePriceBlock,
    decoratePlpCard,
    decoratePdpBlock,
    helpers: {
      buildIndex,
      matchGroup5,
      matchGroup6,
      pickTierForQuantity,
      pickPlpMessage,
      calcUnitPriceCents,
      applyFlatDiscount,
      formatCurrency,
      quantityRangeLabel,
      volumeTierText,
      isFlatRule,
      quantityInRange,
      truncate,
      SOURCE,
    },
  };
}());

function parseMoneyToCents(text) {
  if (!text) return NaN;
  const cleaned = text.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  const num = parseFloat(cleaned);
  if (!Number.isFinite(num)) return NaN;
  return Math.round(num * 100);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));
}

function decorateAllPlpCards(root) {
  const scope = root || document;
  scope.querySelectorAll('.product-card-wrapper[data-product-id], .card-wrapper[data-product-id]').forEach((card) => {
    CeresB2BDiscount.decoratePlpCard(card);
  });
}

function decorateAllPdpBlocks(root) {
  const scope = root || document;
  scope.querySelectorAll('.b2b-pdp-discount[data-product-id]').forEach((block) => {
    CeresB2BDiscount.decoratePdpBlock(block);
  });
}

function watchDynamicContent() {
  if (!('MutationObserver' in window)) return;
  const observer = new MutationObserver((mutations) => {
    let needsPlp = false;
    mutations.forEach((m) => {
      m.addedNodes && m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.matches && (node.matches('.product-card-wrapper') || node.matches('.card-wrapper'))) needsPlp = true;
        if (node.querySelector && node.querySelector('.product-card-wrapper, .card-wrapper')) needsPlp = true;
      });
    });
    if (needsPlp) decorateAllPlpCards();
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

function bootstrap() {
  if (!CeresB2BDiscount.isActive()) return;
  decorateAllPlpCards();
  decorateAllPdpBlocks();
  watchDynamicContent();
}

if (typeof window !== 'undefined') {
  window.CeresB2BDiscount = CeresB2BDiscount;
}

export default function initB2BDiscountMessages() {
  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap, { once: true });
  } else {
    bootstrap();
  }
}

export {
  CeresB2BDiscount,
  decorateAllPlpCards,
  decorateAllPdpBlocks,
  parseMoneyToCents,
  formatCurrency,
};
